/**
 * `Licence` — one serial's chain, at `/countries/:cc/licences/by-serial/:serial`.
 *
 * ===========================================================================
 * ⚠ THIS SCREEN RENDERS A HISTORY. IT MUST NEVER RENDER AN OWNER.
 * ===========================================================================
 * MEASURED on the owner's workbook: 230 serials carry more than one canonical
 * customer and 22 carry three (none carries more), and the file cannot say which of those
 * changes were resales and which were spelling. So the strongest true statement
 * about a serial is the ORDERED LIST of everybody it has been billed to — and the
 * last link is labelled `Latest`, which is a position in that list, rather than
 * `Owner`, which would be a claim about today that is wrong 230 times.
 *
 * (230 is on the merged canonical names the register stores; the design measured
 * 231 on the observed names, and the owner's Parth merge is the one difference.)
 *
 * The payload makes the mistake hard rather than merely discouraged: the licence
 * object carries no `customer_id` at all, and the only holder fact on the wire is
 * `customers[].is_latest`, which cannot be read without the chain around it.
 *
 * ===========================================================================
 * THE RAW CELLS ARE RENDERED BESIDE THE NORMALISED EVENT, ON PURPOSE
 * ===========================================================================
 * `source_sheet`, `source_row`, `source_date_text`, `source_product_text` and
 * `source_party_text` are the workbook's own strings. Normalisation is a CLAIM —
 * that this cell meant that product, that this text meant that date — and the
 * only way an operator can audit a claim is against the evidence it was made
 * from. A screen that showed the tidy name and hid the cell would be asking to be
 * trusted about 3,838 decisions nobody could check. The sheet/row pair is also
 * exactly what somebody needs to find the line in the file.
 *
 * ===========================================================================
 * NO ACTIVATION KEY, ANYWHERE, IN ANY STATE
 * ===========================================================================
 * The register stores none. There is therefore no field, no blank cell and no
 * "hidden" affordance — a masked-looking placeholder would imply the system holds
 * a value it is choosing not to show, and it holds nothing. The payload's own
 * disclosure says it in words, and this screen prints that sentence rather than
 * paraphrasing it.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Chip, EmptyState, SkeletonRows } from '../../components/ui';
import { cn } from '../../lib/cn';
import { fetchLicenceBySerial, licenceErrorText, licenceKeys } from './api';
import type { LicenceClassification, TallyLicenceEventRow } from './api';

const CLASSIFICATION_LABEL: Readonly<Record<string, string>> = {
  customer: 'Customer',
  reseller: 'Reseller',
  group_entity: 'Group entity',
  undecided: 'Undecided',
};

function classificationChip(value: LicenceClassification | null): React.JSX.Element | null {
  if (value === null) return null;
  return (
    <Chip
      className={cn(
        value === 'group_entity' && 'bg-info-bg text-info-fg',
        value === 'undecided' && 'bg-warn-bg text-warn-fg',
      )}
    >
      {CLASSIFICATION_LABEL[value] ?? value}
    </Chip>
  );
}

/** The workbook's own cells, as a single evidence line under the tidy row. */
function evidence(event: TallyLicenceEventRow): string {
  const parts: string[] = [];
  if (event.source_sheet !== null) parts.push(event.source_sheet);
  if (event.source_row !== null) parts.push(`row ${String(event.source_row)}`);
  if (event.source_date_text !== null) parts.push(event.source_date_text);
  if (event.source_party_text !== null) parts.push(event.source_party_text);
  if (event.source_product_text !== null) parts.push(event.source_product_text);
  return parts.join(' · ');
}

export function SerialLookupPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const { serial = '' } = useParams<{ serial: string }>();

  const canView = can(countryCode, 'quotes.view');

  const chain = useQuery({
    queryKey: licenceKeys.serial(countryCode, serial),
    queryFn: ({ signal }) => fetchLicenceBySerial(countryCode, serial, signal),
    enabled: canView && serial !== '',
  });

  if (!canView) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="The licence register is not available to this account">
          Reading a licence in {countryCode} needs the quotes permission. Ask an administrator
          to widen your access.
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title={serial}
        subtitle={
          chain.data === undefined
            ? undefined
            : `${String(chain.data.event_count)} ${chain.data.event_count === 1 ? 'event' : 'events'} · first ${chain.data.licence.first_event_on} · last ${chain.data.licence.last_event_on}`
        }
        actions={
          <Link
            to={`/countries/${countryCode}/licences`}
            className="inline-flex items-center gap-1.5 text-label font-medium text-indigo underline-offset-2 hover:underline"
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            All licences
          </Link>
        }
      />

      {chain.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={4} label="Loading the licence" />
        </div>
      ) : chain.isError ? (
        <Alert tone="danger" title="This licence could not be loaded">
          <p>{licenceErrorText(chain.error)}</p>
        </Alert>
      ) : (
        <>
          {chain.data.changed_hands ? (
            /*
             * Said out loud, in the one place it changes how everything below reads.
             * `info`, not `warning`: a serial changing hands is ordinary in this
             * estate — it happened to 230 of them — and colouring it as a problem
             * would invent a defect out of a fact.
             */
            <Alert tone="info" title="This serial is a history, not an owner">
              It has carried {String(chain.data.customer_count)} customers. The file does not
              say which changes were resales and which were spelling, so the last name below
              is the latest name and nothing more.
            </Alert>
          ) : null}

          <section className="flex flex-col gap-3">
            <h2 className="text-th uppercase text-fg-muted">Customer history</h2>
            <ul
              data-testid="licence-chain"
              className="flex flex-col rounded-md border border-line bg-surface"
            >
              {chain.data.customers.map((link, index) => (
                <li
                  key={link.customer_id ?? `unnamed-${String(index)}`}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line px-5 py-3.5 last:border-b-0"
                >
                  <span className="text-body font-medium text-fg">
                    {link.canonical_name ?? 'Not recorded'}
                  </span>
                  {classificationChip(link.classification)}
                  {link.is_latest ? (
                    <Chip className="bg-ok-bg text-ok-fg">Latest</Chip>
                  ) : null}
                  <span className="tnum text-label text-fg-secondary">
                    {link.first_event_on} → {link.last_event_on}
                  </span>
                  <span className="text-label text-fg-secondary">
                    {String(link.event_count)} {link.event_count === 1 ? 'event' : 'events'}
                  </span>
                  {link.dispute_codes.map((code) => (
                    <Chip key={code} className="bg-warn-bg text-warn-fg">
                      {code}
                    </Chip>
                  ))}
                </li>
              ))}
            </ul>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-th uppercase text-fg-muted">Events</h2>
            <div
              data-testid="licence-events"
              className="overflow-x-auto rounded-md border border-line bg-surface"
            >
              {chain.data.events.length === 0 ? (
                <EmptyState title="This licence carries no events." />
              ) : (
                <table className="w-full min-w-[720px] text-left text-body">
                  <thead className="border-b border-line text-label uppercase text-fg-muted">
                    <tr>
                      <th scope="col" className="px-4 py-2.5 font-medium">
                        Date
                      </th>
                      <th scope="col" className="px-4 py-2.5 font-medium">
                        Customer
                      </th>
                      <th scope="col" className="px-4 py-2.5 font-medium">
                        Product
                      </th>
                      <th scope="col" className="px-4 py-2.5 text-right font-medium">
                        Qty
                      </th>
                      <th scope="col" className="px-4 py-2.5 font-medium">
                        Source
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {chain.data.events.map((event) => (
                      <tr key={event.id} className="crf-register-row border-b border-line last:border-b-0 align-top">
                        <td className="tnum px-4 py-2.5">{event.event_on}</td>
                        <td className="px-4 py-2.5">{event.customer_name ?? 'Not recorded'}</td>
                        <td className="px-4 py-2.5">
                          <span className="flex flex-col gap-1">
                            <span>{event.product_name ?? 'Not recorded'}</span>
                            {/* The evidence line — the cells the normalisation was made from. */}
                            <span className="text-label text-fg-muted">{evidence(event)}</span>
                          </span>
                        </td>
                        <td className="tnum px-4 py-2.5 text-right">{String(event.qty)}</td>
                        <td className="px-4 py-2.5">
                          <span className="flex flex-wrap items-center gap-1.5">
                            <Chip>
                              {event.event_source === 'crf' ? 'Sold as a CRF' : 'Imported'}
                            </Chip>
                            {event.dispute_codes.map((code) => (
                              <Chip key={code} className="bg-warn-bg text-warn-fg">
                                {code}
                              </Chip>
                            ))}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {chain.data.disclosures.length === 0 ? null : (
            <ul data-testid="disclosures" className="flex flex-col gap-1.5">
              {chain.data.disclosures.map((line) => (
                <li key={line} className="text-label text-fg-secondary">
                  {line}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
